/** Browser-only POS queue when cloud is down and Local Engine is not installed. */
const DB_NAME = "godo-pos-offline-v1";
const STORE = "receipts";
const DB_VERSION = 1;

export interface BrowserPosReceipt {
  id: string;
  body: Record<string, unknown>;
  createdAt: string;
  attempts: number;
  lastError?: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function slugPart(raw: string, maxLen: number): string {
  const s = raw.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return (s.slice(0, maxLen) || "X").padEnd(Math.min(maxLen, 3), "X");
}

/** Provisional number — unique per browser profile + user (INV-OFF-BRW-…). */
export function nextBrowserOfflineInvoiceNo(orgId: string, userId: string): string {
  const scope = `${orgId.trim()}:${userId.trim()}`;
  const key = `godo_pos_offline_seq:${scope}`;
  const prev = Number(localStorage.getItem(key) ?? "0");
  const seq = Number.isFinite(prev) ? prev + 1 : 1;
  localStorage.setItem(key, String(seq));
  const user = slugPart(userId, 6);
  return `INV-OFF-BRW-${user}-${String(seq).padStart(6, "0")}`;
}

export async function queuePosReceiptInBrowser(
  body: Record<string, unknown>,
): Promise<{ id: string; queuedAt: string }> {
  const id = `brw_${crypto.randomUUID().replace(/-/g, "")}`;
  const record: BrowserPosReceipt = {
    id,
    body,
    createdAt: new Date().toISOString(),
    attempts: 0,
  };
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB write failed"));
  });
  db.close();
  return { id, queuedAt: record.createdAt };
}

export async function listBrowserPosReceipts(): Promise<BrowserPosReceipt[]> {
  const db = await openDb();
  const rows = await new Promise<BrowserPosReceipt[]>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve((req.result as BrowserPosReceipt[]) ?? []);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB read failed"));
  });
  db.close();
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function countBrowserPosPending(): Promise<number> {
  const rows = await listBrowserPosReceipts();
  return rows.length;
}

async function removeBrowserPosReceipt(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB delete failed"));
  });
  db.close();
}

async function touchAttempt(id: string, lastError: string): Promise<void> {
  const db = await openDb();
  const row = await new Promise<BrowserPosReceipt | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => resolve(req.result as BrowserPosReceipt | undefined);
    req.onerror = () => reject(req.error);
  });
  if (row) {
    row.attempts += 1;
    row.lastError = lastError;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(row);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  db.close();
}

function isRetryableNetworkError(err: unknown): boolean {
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  if (typeof err === "object" && err !== null && (err as { reason?: string }).reason) {
    const reason = (err as { reason?: string }).reason;
    return reason === "network_error" || reason === "timeout";
  }
  return false;
}

/** Push queued browser sales to cloud when online. */
export async function flushBrowserPosQueue(
  postCollect: (body: Record<string, unknown>) => Promise<unknown>,
): Promise<{ synced: number; failed: number; pending: number }> {
  const rows = await listBrowserPosReceipts();
  let synced = 0;
  let failed = 0;
  for (const row of rows) {
    try {
      await postCollect(row.body);
      await removeBrowserPosReceipt(row.id);
      synced++;
    } catch (err) {
      failed++;
      const msg = err instanceof Error ? err.message : String(err);
      await touchAttempt(row.id, msg);
      if (isRetryableNetworkError(err)) break;
    }
  }
  const pending = await countBrowserPosPending();
  return { synced, failed, pending };
}
